import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon  from '@mui/icons-material/ArrowBackIosNewRounded';
import AutoAwesomeRoundedIcon      from '@mui/icons-material/AutoAwesomeRounded';
import CalendarTodayRoundedIcon    from '@mui/icons-material/CalendarTodayRounded';
import CheckCircleRoundedIcon      from '@mui/icons-material/CheckCircleRounded';
import DiamondRoundedIcon          from '@mui/icons-material/DiamondRounded';
import HistoryRoundedIcon          from '@mui/icons-material/HistoryRounded';
import NotesRoundedIcon            from '@mui/icons-material/NotesRounded';
import StarRoundedIcon             from '@mui/icons-material/StarRounded';

import { getBillingHistory } from '../../api/index';

import './styles.scss';

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

const planMeta = (paymentType) => {
  switch (paymentType) {
    case 'one_time':  return { label: 'Pro',      color: '#7c3aed', Icon: StarRoundedIcon    };
    case 'recurring': return { label: 'Pro Plus',  color: '#0ea5e9', Icon: DiamondRoundedIcon };
    default:          return { label: 'Basic',     color: '#6b7280', Icon: NotesRoundedIcon   };
  }
};

const PERIOD_LABEL = { monthly: 'Monthly', yearly: 'Yearly', lifetime: 'Lifetime', free: 'Free' };

const ProfilePage = () => {
  const nav          = useNavigate();
  const user         = useSelector(state => state.auth.user);
  const subscription = user?.subscription;

  const [history,     setHistory]     = useState([]);
  const [histLoading, setHistLoading] = useState(true);

  useEffect(() => {
    getBillingHistory()
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory([]))
      .finally(() => setHistLoading(false));
  }, []);

  const meta = planMeta(subscription?.plan?.payment_type);
  const PlanIcon = meta.Icon;

  return (
    <>
      <Helmet>
        <title>Profile — NoteCORE</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Container maxWidth="sm" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>

      {/* Back */}
      <Button
        startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: '16px !important' }} />}
        onClick={() => nav('/app')}
        sx={{ fontWeight: 600, borderRadius: '10px', textTransform: 'none', fontSize: 14, mb: 3 }}
      >
        Back to Notes
      </Button>

      {/* ── Profile card ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '24px',
          border: '1.5px solid',
          borderColor: 'divider',
          p: 3,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
        }}
      >
        <Avatar
          src={user?.picture}
          alt={user?.name}
          sx={{ width: 64, height: 64 }}
        />
        <Box flex={1} minWidth={0}>
          <Typography variant="h6" fontWeight={800} noWrap>
            {user?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
          <Box mt={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Chip
              icon={<PlanIcon sx={{ fontSize: '14px !important', color: `${meta.color} !important` }} />}
              label={meta.label}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 12,
                bgcolor: `${meta.color}18`,
                color: meta.color,
                border: `1px solid ${meta.color}40`,
              }}
            />
            <Chip
              icon={<CheckCircleRoundedIcon sx={{ fontSize: '14px !important' }} />}
              label="Active"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: 12 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* ── Plan details ── */}
      <Paper
        elevation={0}
        sx={{ borderRadius: '24px', border: '1.5px solid', borderColor: 'divider', p: 3, mb: 3 }}
      >
        <Typography variant="subtitle1" fontWeight={700} mb={2} display="flex" alignItems="center" gap={1}>
          <AutoAwesomeRoundedIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
          Current Plan
        </Typography>

        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <InfoRow label="Plan" value={subscription?.plan?.name ?? 'Basic'} />
          <InfoRow label="Billing" value={PERIOD_LABEL[subscription?.billing_period] ?? '—'} />
          <InfoRow
            label="Member since"
            value={fmt(user?.created_at)}
            icon={<CalendarTodayRoundedIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
          />
          <InfoRow
            label="Renews / Expires"
            value={subscription?.expires_at ? fmt(subscription.expires_at) : subscription?.billing_period === 'lifetime' ? 'Never ✓' : '—'}
          />
          <InfoRow
            label="Notes limit"
            value={subscription?.plan?.max_notes === -1 ? 'Unlimited' : `${subscription?.plan?.max_notes ?? 3} notes`}
          />
          <InfoRow label="Status" value={subscription?.status ?? 'active'} capitalize />
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Button
          variant="outlined"
          fullWidth
          onClick={() => nav('/pricing')}
          startIcon={<AutoAwesomeRoundedIcon />}
          sx={{ borderRadius: '12px', fontWeight: 700, borderColor: '#7c3aed', color: '#7c3aed',
            '&:hover': { bgcolor: 'rgba(124,58,237,0.06)', borderColor: '#7c3aed' } }}
        >
          View Plans &amp; Upgrade
        </Button>
      </Paper>

      {/* ── Billing history ── */}
      <Paper
        elevation={0}
        sx={{ borderRadius: '24px', border: '1.5px solid', borderColor: 'divider', p: 3 }}
      >
        <Typography variant="subtitle1" fontWeight={700} mb={2} display="flex" alignItems="center" gap={1}>
          <HistoryRoundedIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
          Billing History
        </Typography>

        {histLoading ? (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {[1, 2].map(i => <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: '12px' }} />)}
          </Box>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            No payments yet.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {history.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.5,
                  borderRadius: '14px',
                  background: 'rgba(124,58,237,0.04)',
                  border: '1px solid rgba(124,58,237,0.1)',
                  gap: 1,
                }}
              >
                <Box minWidth={0}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {item.plan_name}&nbsp;
                    <Box component="span" sx={{ fontWeight: 500, fontSize: 11, color: 'text.secondary' }}>
                      {PERIOD_LABEL[item.billing_period] ?? item.billing_period}
                    </Box>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmt(item.paid_at)}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={800} sx={{ color: '#7c3aed', flexShrink: 0 }}>
                  ${Number(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* ── Legal links ── */}
      <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <Typography
          variant="caption"
          component={RouterLink}
          to="/terms"
          sx={{
            color: 'text.disabled',
            textDecoration: 'none',
            fontSize: 12,
            fontWeight: 500,
            transition: 'color 0.15s ease',
            '&:hover': { color: 'text.secondary' },
          }}
        >
          Terms of Service
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 12 }}>·</Typography>
        <Typography
          variant="caption"
          component={RouterLink}
          to="/privacy"
          sx={{
            color: 'text.disabled',
            textDecoration: 'none',
            fontSize: 12,
            fontWeight: 500,
            transition: 'color 0.15s ease',
            '&:hover': { color: 'text.secondary' },
          }}
        >
          Privacy Policy
        </Typography>
      </Box>

    </Container>
    </>
  );
};

const InfoRow = ({ label, value, icon, capitalize }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.6}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={700}
      mt={0.25}
      display="flex"
      alignItems="center"
      gap={0.5}
      sx={{ textTransform: capitalize ? 'capitalize' : 'none' }}
    >
      {icon}{value}
    </Typography>
  </Box>
);

export default ProfilePage;
